import React from 'react';
import { useTranslation } from 'react-i18next';
import { KPI } from '@heroui-pro/react/kpi';
import { KPIGroup } from '@heroui-pro/react';
import { Card, type TypographyProps } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DoneImports } from '../../doneImports';

type ImportTotalsProps = {
    totals: DoneImports['totals'];
    /** A phone: the figures stack. */
    isNarrow: boolean;
};

// The card's title sits beside the branches' headings, under the step's title.
const asH2: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h2 {...domProps}>{children}</h2>;

const TOTAL_FIGURES = ['rows', 'products', 'units', 'stockValue'] as const;

type TotalFigure = (typeof TOTAL_FIGURES)[number];

// Literal keys, so the locale guards see every one of them.
const TOTAL_LABEL_KEY = {
    rows: 'onboarding.done.totals.rows',
    products: 'onboarding.done.totals.products',
    units: 'onboarding.done.totals.units',
    stockValue: 'onboarding.done.totals.stockValue',
} as const satisfies Record<TotalFigure, string>;

/**
 * The totals across branches, standing imports only: how many imports in
 * how many branches, then rows, products, units and the stock's net value as
 * HeroUI Pro KPIs (Result's figures), stacked on a phone.
 */
export const ImportTotals: React.FC<ImportTotalsProps> = ({
    totals,
    isNarrow,
}) => {
    const { t } = useTranslation('pages');

    const values: Record<TotalFigure, { value: number; text: string }> = {
        rows: { value: totals.rows, text: countParam(totals.rows) },
        products: { value: totals.products, text: countParam(totals.products) },
        units: { value: totals.units, text: countParam(totals.units) },
        stockValue: {
            value: totals.stockValueTetri,
            text: formatGel(totals.stockValueTetri),
        },
    };

    return (
        <Card
            className="gap-4 p-4 sm:p-6"
            {...getNodeTestIdProp('onboarding-done-totals')}
        >
            <Card.Header className="gap-1">
                <Card.Title render={asH2}>
                    {t('onboarding.done.totals.title')}
                </Card.Title>
                <Card.Description
                    {...getNodeTestIdProp('onboarding-done-totals-meta')}
                >
                    {[
                        t('onboarding.done.totals.imports', {
                            count: totals.imports,
                            total: countParam(totals.imports),
                        }),
                        t('onboarding.done.totals.branches', {
                            count: totals.branches,
                            total: countParam(totals.branches),
                        }),
                    ].join(' · ')}
                </Card.Description>
            </Card.Header>
            <Card.Content>
                <KPIGroup orientation={isNarrow ? 'vertical' : 'horizontal'}>
                    {TOTAL_FIGURES.map((figure, index) => (
                        <React.Fragment key={figure}>
                            {index > 0 ? <KPIGroup.Separator /> : null}
                            <KPI
                                {...getNodeTestIdProp(
                                    `onboarding-done-total-${figure}`,
                                )}
                            >
                                <KPI.Header>
                                    <KPI.Title>
                                        {t(TOTAL_LABEL_KEY[figure])}
                                    </KPI.Title>
                                </KPI.Header>
                                <KPI.Content>
                                    <KPI.Value value={values[figure].value}>
                                        {() => values[figure].text}
                                    </KPI.Value>
                                </KPI.Content>
                            </KPI>
                        </React.Fragment>
                    ))}
                </KPIGroup>
            </Card.Content>
        </Card>
    );
};
